import { Link, useNavigate } from "react-router-dom";

import {
  ShoppingCart,
  Search,
  User,
  X,
  Home,
  LayoutGrid,
  BookOpen,
  MapPin,
  ChevronRight,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import API from "../../services/api";
import logo from "../../assets/logo.png";

import "../../styles/navbar.css";


export default function Navbar() {
  const navigate = useNavigate();

  const [cartCount, setCartCount] = useState(0);
  const [loadingCart, setLoadingCart] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");


  /* =========================================================
     AUTENTICACIÓN
  ========================================================= */

  const isAuthenticated = useCallback(() => {
    try {
      const auth = JSON.parse(
        localStorage.getItem("auth") || "null"
      );

      return Boolean(auth?.access);
    } catch {
      return false;
    }
  }, []);


  /* =========================================================
     OBTENER CANTIDAD DEL CARRITO LOCAL
  ========================================================= */

  const getLocalCartCount = useCallback(() => {
    try {
      const savedCart = JSON.parse(
        localStorage.getItem("cart") || "[]"
      );

      if (!Array.isArray(savedCart)) {
        return 0;
      }

      return savedCart.reduce(
        (total, item) => {
          const quantity = Number(
            item?.quantity ??
            item?.qty ??
            1
          );

          return total + Math.max(1, quantity);
        },
        0
      );
    } catch (error) {
      console.error(
        "Error leyendo carrito local:",
        error
      );

      return 0;
    }
  }, []);


  /* =========================================================
     OBTENER CARRITO

     - Invitado → localStorage
     - Usuario autenticado → localStorage + API
  ========================================================= */

  const fetchCart = useCallback(async () => {
    try {
      setLoadingCart(true);


      /* =====================================================
         CARRITO LOCAL
      ===================================================== */

      const localCount = getLocalCartCount();

      setCartCount(localCount);


      /* =====================================================
         SI NO ESTÁ AUTENTICADO

         El carrito de invitado funciona con localStorage.
      ===================================================== */

      if (!isAuthenticated()) {
        return;
      }


      /* =====================================================
         CARRITO DEL BACKEND
      ===================================================== */

      try {
        const response = await API.get("/cart/");

        const items =
          response.data?.items ||
          response.data?.data?.items ||
          [];

        if (!Array.isArray(items)) {
          return;
        }


        /* ===================================================
           CANTIDAD DEL CARRITO API
        =================================================== */

        const apiTotal = items.reduce(
          (total, item) => {
            const quantity = Number(
              item?.quantity ??
              item?.qty ??
              1
            );

            return total + Math.max(1, quantity);
          },
          0
        );


        /* ===================================================
           PRIORIDAD DEL CARRITO LOCAL

           Si existe carrito local, lo mantenemos.
        =================================================== */

        const hasLocalCart =
          getLocalCartCount() > 0;

        if (!hasLocalCart) {
          setCartCount(apiTotal);
        }

      } catch (error) {
        console.warn(
          "No fue posible cargar el carrito del servidor:",
          error
        );
      }

    } catch (error) {
      console.error(
        "Error cargando carrito:",
        error
      );

      setCartCount(0);

    } finally {
      setLoadingCart(false);
    }

  }, [
    getLocalCartCount,
    isAuthenticated,
  ]);


  /* =========================================================
     ACTUALIZAR CARRITO
  ========================================================= */

  useEffect(() => {
    fetchCart();

    const handleCartUpdate = () => {
      fetchCart();
    };

    const handleStorage = () => {
      fetchCart();
    };

    window.addEventListener(
      "cartUpdated",
      handleCartUpdate
    );

    window.addEventListener(
      "storage",
      handleStorage
    );

    return () => {
      window.removeEventListener(
        "cartUpdated",
        handleCartUpdate
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );
    };

  }, [fetchCart]);


  /* =========================================================
     BLOQUEAR SCROLL CON MENÚ ABIERTO
  ========================================================= */

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add(
        "navbar-menu-open"
      );
    } else {
      document.body.classList.remove(
        "navbar-menu-open"
      );
    }

    return () => {
      document.body.classList.remove(
        "navbar-menu-open"
      );
    };

  }, [menuOpen]);


  /* =========================================================
     CERRAR MENÚ
  ========================================================= */

  const closeMenu = () => {
    setMenuOpen(false);
  };


  /* =========================================================
     ABRIR / CERRAR MENÚ
  ========================================================= */

  const toggleMenu = () => {
    setMenuOpen((previous) => !previous);
  };


  /* =========================================================
     BUSCADOR DESKTOP
  ========================================================= */

  const handleSearch = (event) => {
    event.preventDefault();

    const value = search.trim();

    if (!value) {
      navigate("/catalog");
      return;
    }

    navigate(
      `/catalog?search=${encodeURIComponent(value)}`
    );

    setSearch("");
  };


  /* =========================================================
     BUSCADOR MÓVIL
  ========================================================= */

  const handleMobileSearch = () => {
    navigate("/catalog");
    closeMenu();
  };


  /* =========================================================
     IR AL CARRITO

     El carrito es público.
  ========================================================= */

  const goToCart = () => {
    closeMenu();

    navigate("/cart");
  };


  /* =========================================================
     IR AL PERFIL

     El perfil requiere autenticación.
  ========================================================= */

  const goToProfile = () => {
    closeMenu();

    if (!isAuthenticated()) {
      localStorage.setItem(
        "redirectAfterLogin",
        "/profile"
      );

      navigate("/login", {
        state: {
          from: "/profile",
        },
      });

      return;
    }

    navigate("/profile");
  };


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <header className="navbar">


        {/* ===================================================
            LOGO
        =================================================== */}

        <Link
          to="/"
          className="navbar-logo"
          onClick={closeMenu}
          aria-label="Debuts Crop - Inicio"
        >
          <img
            src={logo}
            alt="Debuts Crop"
          />
        </Link>


        {/* ===================================================
            NAVEGACIÓN DESKTOP
        =================================================== */}

        <nav
          className="navbar-nav"
          aria-label="Navegación principal"
        >


          {/* =================================================
              INICIO
          ================================================= */}

          <Link
            to="/"
            className="navbar-action navbar-nav-action"
            aria-label="Inicio"
            data-tooltip="Inicio"
          >
            <Home size={21} />
          </Link>


          {/* =================================================
              CATÁLOGO
          ================================================= */}

          <Link
            to="/catalog"
            className="navbar-action navbar-nav-action"
            aria-label="Catálogo"
            data-tooltip="Catálogo"
          >
            <LayoutGrid size={21} />
          </Link>


          {/* =================================================
              BLOG
          ================================================= */}

          <Link
            to="/blog"
            className="navbar-action navbar-nav-action"
            aria-label="Blog"
            data-tooltip="Blog"
          >
            <BookOpen size={21} />
          </Link>


          {/* =================================================
              SEDES
          ================================================= */}

          <Link
            to="/sedes"
            className="navbar-action navbar-nav-action navbar-stores-action"
            aria-label="Sedes"
            data-tooltip="Sedes"
          >
            <MapPin size={21} />
          </Link>

        </nav>


        {/* ===================================================
            BUSCADOR DESKTOP
        =================================================== */}

        <form
          className="navbar-search"
          onSubmit={handleSearch}
        >

          <Search
            size={19}
            className="navbar-search-icon"
          />

          <input
            type="text"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Buscar productos, marcas o categorías..."
            aria-label="Buscar productos"
          />

          <button
            type="submit"
            className="navbar-search-button"
            aria-label="Buscar"
            data-tooltip="Buscar"
          >
            <Search size={17} />
          </button>

        </form>


        {/* ===================================================
            ACCIONES
        =================================================== */}

        <div className="navbar-actions">


          {/* =================================================
              BUSCADOR MÓVIL
          ================================================= */}

          <button
            type="button"
            className="navbar-action navbar-mobile-search"
            onClick={handleMobileSearch}
            aria-label="Buscar"
            data-tooltip="Buscar"
          >
            <Search size={21} />
          </button>


          {/* =================================================
              CARRITO
          ================================================= */}

          <button
            type="button"
            className="navbar-action navbar-desktop-action"
            onClick={goToCart}
            aria-label="Carrito"
            data-tooltip="Carrito"
          >

            <ShoppingCart size={21} />

            {cartCount > 0 && (
              <span className="navbar-cart-badge">
                {loadingCart
                  ? "..."
                  : cartCount}
              </span>
            )}

          </button>


          {/* =================================================
              PERFIL
          ================================================= */}

          <button
            type="button"
            className="navbar-action navbar-desktop-action"
            onClick={goToProfile}
            aria-label="Mi cuenta"
            data-tooltip="Mi perfil"
          >
            <User size={21} />
          </button>


          {/* =================================================
              HAMBURGUESA
          ================================================= */}

          <button
            type="button"
            className={`navbar-hamburger ${
              menuOpen ? "active" : ""
            }`}
            onClick={toggleMenu}
            aria-label={
              menuOpen
                ? "Cerrar menú"
                : "Abrir menú"
            }
            aria-expanded={menuOpen}
            data-tooltip={
              menuOpen
                ? "Cerrar menú"
                : "Abrir menú"
            }
          >

            <span></span>
            <span></span>
            <span></span>

          </button>

        </div>

      </header>


      {/* =====================================================
          OVERLAY
      ===================================================== */}

      <div
        className={`navbar-overlay ${
          menuOpen ? "active" : ""
        }`}
        onClick={closeMenu}
        aria-hidden="true"
      />


      {/* =====================================================
          MENÚ LATERAL MOBILE
      ===================================================== */}

      <aside
        className={`navbar-drawer ${
          menuOpen ? "active" : ""
        }`}
        aria-hidden={!menuOpen}
      >


        {/* ===================================================
            HEADER DRAWER
        =================================================== */}

        <div className="navbar-drawer-header">

          <Link
            to="/"
            className="navbar-drawer-logo"
            onClick={closeMenu}
          >

            <img
              src={logo}
              alt="Debuts Crop"
            />

          </Link>


          <button
            type="button"
            className="navbar-drawer-close"
            onClick={closeMenu}
            aria-label="Cerrar menú"
            data-tooltip="Cerrar menú"
          >
            <X size={25} />
          </button>

        </div>


        {/* ===================================================
            LÍNEA DECORATIVA
        =================================================== */}

        <div className="navbar-drawer-line" />


        {/* ===================================================
            NAVEGACIÓN MOBILE
        =================================================== */}

        <nav
          className="navbar-mobile-nav"
          aria-label="Navegación móvil"
        >


          {/* =================================================
              INICIO
          ================================================= */}

          <Link
            to="/"
            className="navbar-mobile-link"
            onClick={closeMenu}
          >

            <span className="navbar-mobile-icon">
              <Home size={20} />
            </span>

            <span className="navbar-mobile-label">
              Inicio
            </span>

            <ChevronRight size={18} />

          </Link>


          {/* =================================================
              CATÁLOGO
          ================================================= */}

          <Link
            to="/catalog"
            className="navbar-mobile-link"
            onClick={closeMenu}
          >

            <span className="navbar-mobile-icon">
              <LayoutGrid size={20} />
            </span>

            <span className="navbar-mobile-label">
              Catálogo
            </span>

            <ChevronRight size={18} />

          </Link>


          {/* =================================================
              BLOG
          ================================================= */}

          <Link
            to="/blog"
            className="navbar-mobile-link"
            onClick={closeMenu}
          >

            <span className="navbar-mobile-icon">
              <BookOpen size={20} />
            </span>

            <span className="navbar-mobile-label">
              Blog
            </span>

            <ChevronRight size={18} />

          </Link>


          {/* =================================================
              SEDES
          ================================================= */}

          <Link
            to="/sedes"
            className="navbar-mobile-link navbar-mobile-stores-link"
            onClick={closeMenu}
          >

            <span className="navbar-mobile-icon">
              <MapPin size={20} />
            </span>

            <span className="navbar-mobile-label">
              Sedes
            </span>

            <ChevronRight size={18} />

          </Link>


          {/* =================================================
              CARRITO
          ================================================= */}

          <button
            type="button"
            className="navbar-mobile-link"
            onClick={goToCart}
          >

            <span className="navbar-mobile-icon">
              <ShoppingCart size={20} />
            </span>

            <span className="navbar-mobile-label">
              Carrito
            </span>

            {cartCount > 0 && (
              <span className="navbar-mobile-count">
                {loadingCart
                  ? "..."
                  : cartCount}
              </span>
            )}

            <ChevronRight size={18} />

          </button>


          {/* =================================================
              PERFIL
          ================================================= */}

          <button
            type="button"
            className="navbar-mobile-link"
            onClick={goToProfile}
          >

            <span className="navbar-mobile-icon">
              <User size={20} />
            </span>

            <span className="navbar-mobile-label">
              Mi perfil
            </span>

            <ChevronRight size={18} />

          </button>

        </nav>


        {/* ===================================================
            FOOTER DRAWER
        =================================================== */}

        <div className="navbar-drawer-footer">

          <strong>
            DEBUTS CROP
          </strong>

          <span>
            TU CULTIVO · TU ESTILO
          </span>

        </div>

      </aside>
    </>
  );
}